import {
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
  Button,
  Chip,
  Tabs,
  Tab,
  Popover,
  TextField,
} from "@mui/material";
import { useState, useEffect } from "react";
import { isMobile } from "react-device-detect";
import { useGetUserDetailsMutation } from "../../../redux/api/authApi";
import { getCookie } from "utils/storageUtils";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CleaningServicesOutlinedIcon from "@mui/icons-material/CleaningServicesOutlined";

// ── icons for small stat cards ──
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import ContentCutOutlinedIcon from "@mui/icons-material/ContentCutOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import VolumeUpOutlinedIcon from "@mui/icons-material/VolumeUpOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";

const cardBg = "#15151D";
const border = "#1f2230";

// Inter font — applied consistently across all text elements
const interFont = "'Inter', sans-serif";

const timeTabs = [
  { label: "7 Days", value: "7days" },
  { label: "30 Days", value: "30days" },
  { label: "90 Days", value: "90days" },
  { label: "1 Year", value: "1year" },
];

// ─────────────────────────────────────────────
// DUMMY DATA — shown when real data is empty
// ─────────────────────────────────────────────
const DUMMY_DATA = {
  paid_learners: 128,
  total_learners: 342,
  total_courses: 18,
  digital_twins: 5,
  total_episodes: 97,
  total_blogs: 43,
  total_audios: 22,
  total_documents: 11,
  total_clips: 35,
  total_images: 214,
  dashboard: {
    revenue: { total: "24,500", change_percent: 18 },
    learners: { change_percent: 12 },
  },
  charts: {
    paid_learners_chart: {
      labels: ["Apr 1", "Apr 2", "Apr 3", "Apr 4", "Apr 5", "Apr 6", "Apr 7"],
      data: [30, 52, 45, 80, 95, 110, 128],
    },
    revenue_chart: {
      labels: ["Apr 1", "Apr 2", "Apr 3", "Apr 4", "Apr 5", "Apr 6", "Apr 7"],
      data: [3200, 5400, 4100, 8900, 11200, 19800, 24500],
    },
    learners_chart: {
      labels: ["Apr 1", "Apr 2", "Apr 3", "Apr 4", "Apr 5", "Apr 6", "Apr 7"],
      data: [80, 120, 155, 210, 270, 310, 342],
    },
  },
};

// Helper: check if chart data is empty (the key visual that looks broken)
function isChartDataEmpty(data: any): boolean {
  if (!data) return true;
  const learnersChart = data?.charts?.paid_learners_chart?.data ?? [];
  const revenueChart = data?.charts?.revenue_chart?.data ?? [];
  const totalLearnersChart = data?.charts?.learners_chart?.data ?? [];
  const allCharts = [...learnersChart, ...revenueChart, ...totalLearnersChart];
  return allCharts.length === 0 || allCharts.every((v: any) => Number(v) === 0);
}

// ── MiniAreaChart ──
function MiniAreaChart({
  labels = [],
  data = [],
  isDummy = false,
}: {
  labels: string[];
  data: number[];
  isDummy?: boolean;
}) {
  const numericData = data.map((v) => Number(v));
  const hasData = labels.length > 0 && numericData.length > 0;
  const svgWidth = 300;
  const svgHeight = 120;
  const padTop = 10;
  const padBottom = 10;
  const maxVal = hasData ? Math.max(...numericData) : 0;
  const minVal = hasData ? Math.min(...numericData) : 0;
  const range = maxVal - minVal || 1;
  const points = hasData
    ? numericData.map((val, i) => ({
      x: numericData.length > 1 ? (i / (numericData.length - 1)) * svgWidth : svgWidth / 2,
      y: padTop + (1 - (val - minVal) / range) * (svgHeight - padTop - padBottom),
    }))
    : [];
  const linePath = points
    .map((p, i) => {
      if (i === 0) return `M${p.x},${p.y}`;
      const prev = points[i - 1];
      const cpx = (prev.x + p.x) / 2;
      return `C${cpx},${prev.y} ${cpx},${p.y} ${p.x},${p.y}`;
    })
    .join(" ");
  const areaPath =
    hasData && points.length
      ? `${linePath} L${points[points.length - 1].x},${svgHeight} L${points[0].x},${svgHeight} Z`
      : "";
  const last = hasData ? points[points.length - 1] : null;
  const lastVal = hasData ? numericData[numericData.length - 1] : null;
  const displayVal =
    lastVal != null
      ? lastVal >= 1000
        ? `${(lastVal / 1000).toFixed(1)}k`
        : `${lastVal}`
      : "0";
  const yTop = maxVal >= 1000 ? `${Math.round(maxVal / 1000)}K` : `${maxVal}`;
  const yMid = Math.round((maxVal + minVal) / 2);
  const yMidLabel = yMid >= 1000 ? `${Math.round(yMid / 1000)}K` : `${yMid}`;
  const yBot = minVal >= 1000 ? `${Math.round(minVal / 1000)}K` : `${minVal}`;
  const xLabels = hasData
    ? [labels[0], labels[Math.floor(labels.length / 2)], labels[labels.length - 1]]
    : ["-", "-", "-"];

  const lineColor = "#64b5f6";
  const gradStart = "#4dabf7";

  return (
    <Box
      sx={{
        mt: 1.5,
        borderRadius: 2.5,
        p: 1.5,
        background: "linear-gradient(180deg,#1b1d27,#151722)",
        height: 190,
        position: "relative",
      }}
    >
      <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
        {/* Y-axis labels */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 8,
            bottom: 28,
            width: 32,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            fontSize: 11,
            fontFamily: interFont,
            color: "#9aa0b4",
          }}
        >
          <span>{hasData ? yTop : "-"}</span>
          <span>{hasData ? yMidLabel : "-"}</span>
          <span>{hasData ? yBot : "-"}</span>
        </Box>

        {/* Chart SVG */}
        <Box sx={{ position: "absolute", left: 32, right: 6, top: 6, bottom: 28 }}>
          <svg viewBox="0 0 300 120" preserveAspectRatio="none" width="100%" height="100%">
            <defs>
              <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={gradStart} stopOpacity="0.65" />
                <stop offset="100%" stopColor={gradStart} stopOpacity="0" />
              </linearGradient>
            </defs>
            <line x1="0" y1="10" x2="300" y2="10" stroke="#2a2c36" />
            <line x1="0" y1="55" x2="300" y2="55" stroke="#2a2c36" />
            <line x1="0" y1="100" x2="300" y2="100" stroke="#2a2c36" />
            {hasData && points.length ? (
              <>
                <path d={areaPath} fill="url(#areaGrad)" />
                <path d={linePath} fill="none" stroke={lineColor} strokeWidth="2" />
                <line x1={last!.x} y1="0" x2={last!.x} y2="120" stroke={lineColor} strokeOpacity="0.6" />
                <circle cx={last!.x} cy={last!.y} r="4" fill={lineColor} />
                <circle cx={last!.x} cy={last!.y} r="7" fill={lineColor} opacity="0.15" />
              </>
            ) : null}
          </svg>
          {/* Last value badge */}
          <Box
            sx={{
              position: "absolute",
              right: 4,
              top: -8,
              background: "#fff",
              color: "#000",
              fontSize: 11,
              fontFamily: interFont,
              fontWeight: 700,
              px: 1,
              py: 0.3,
              borderRadius: 1,
            }}
          >
            {displayVal}
          </Box>
        </Box>

        {/* X-axis labels */}
        <Box
          sx={{
            position: "absolute",
            left: 32,
            right: 6,
            bottom: 4,
            display: "flex",
            justifyContent: "space-between",
            fontSize: 11,
            fontFamily: interFont,
            color: "#9aa0b4",
          }}
        >
          {xLabels.map((l, i) => (
            <span key={i}>{l}</span>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ── StatCard ──
function StatCard({
  title,
  value,
  growth,
  chartLabels = [],
  chartData = [],
  isDummy = false,
}: {
  title: string;
  value: string;
  growth: string;
  chartLabels?: string[];
  chartData?: number[];
  isDummy?: boolean;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: "20px",
        height: "100%",
        borderRadius: "12px",
        background: cardBg,
        border: `1px solid ${border}`,
        color: "#fff",
      }}
    >
      <Stack spacing={0.6}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {/* Figma: Inter 16px Bold, color #9aa0b4 */}
          <Typography
            fontFamily={interFont}
            fontSize={16}
            fontWeight={700}
            color="#9aa0b4"
            letterSpacing="-0.01em"
          >
            {title}
          </Typography>
          <Box sx={{ color: "#9aa0b4", fontSize: 16, lineHeight: 1 }}>↗</Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          {/* Figma: Inter 32px Bold, white */}
          <Typography
            fontFamily={interFont}
            fontSize={32}
            fontWeight={700}
            lineHeight={1.1}
            letterSpacing="-0.02em"
          >
            {value}
          </Typography>
          <Chip
            label={growth}
            size="small"
            sx={{
              height: 20,
              fontSize: 11,
              fontFamily: interFont,
              fontWeight: 600,
              color: "#32d583",
              background: "rgba(50,213,131,0.15)",
              borderRadius: "6px",
            }}
          />
        </Stack>

        <MiniAreaChart labels={chartLabels} data={chartData} isDummy={isDummy} />
      </Stack>
    </Paper>
  );
}

// ── SmallStat ──
function SmallStat({
  label,
  value,
  icon,
  isDummy = false,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
  isDummy?: boolean;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: "16px 20px",
        borderRadius: "12px",
        background: cardBg,
        border: `1px solid ${border}`,
        color: "#fff",
        height: "100%",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={0.8} alignItems="center">
            {icon && (
              <Box sx={{ color: "#9aa0b4", display: "flex", fontSize: 16 }}>
                {icon}
              </Box>
            )}
            {/* Figma: Inter 16px Bold */}
            <Typography
              fontFamily={interFont}
              fontSize={16}
              fontWeight={700}
              color="#9aa0b4"
              letterSpacing="-0.01em"
            >
              {label}
            </Typography>
          </Stack>
          {/* Figma: Inter 32px Bold */}
          <Typography
            fontFamily={interFont}
            fontSize={32}
            fontWeight={700}
            lineHeight={1.1}
            letterSpacing="-0.02em"
          >
            {value}
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function MyDashboard() {
  const [value, setValue] = useState(0);
  const [getUserDetailsMutation] = useGetUserDetailsMutation();
  const [userDets, setUserDets] = useState<any>();
  const [filter, setFilter] = useState("7days");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // const [showingDummy, setShowingDummy] = useState(true);
  const [showingDummy, setShowingDummy] = useState(() => {
    const isCleared = localStorage.getItem("mockDataCleared") === "true";
    return !isCleared; /* (if cleared, it will don't show dummy) */
  });

  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(6, "day"));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const useremail = localStorage.getItem("user_email") || "";

  const fetchData = async () => {
    try {
      const response = await getUserDetailsMutation({
        email: useremail || getCookie("user_email") || "",
        filter: filter ? filter : "",
        start_date: startDate ? startDate.format("YYYY-MM-DD") : "",
        end_date: endDate ? endDate.format("YYYY-MM-DD") : "",
      });
      if ("data" in response) {
        const realData = response.data.data;
        setUserDets(realData);

        const isCleared = localStorage.getItem("mockDataCleared") === "true";
        if (!isCleared) {
          if (isChartDataEmpty(realData)) {
            setShowingDummy(true);
          } else {
            setShowingDummy(false);
          }
        } else {
          setShowingDummy(false);
        }
        console.log("dashboard res=>", realData);
      }
    } catch (error) { }
  };

  useEffect(() => {
    fetchData();
  }, [filter, startDate, endDate]);

  const displayData = showingDummy ? DUMMY_DATA : userDets;

  return (
    <Box sx={{ minHeight: "100vh", background: "transparent", color: "#fff", fontFamily: interFont }}>
      {/* Header */}
      <Typography fontFamily={interFont} fontSize={26} fontWeight={700} letterSpacing="-0.02em">
        My Dashboard
      </Typography>
      <Typography fontFamily={interFont} fontSize={12} color="#9aa0b4" mb={3}>
        Here you can find account summary dashboard
      </Typography>

      {/* Sample data banner */}
      {showingDummy && (
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1.2,
            borderRadius: "10px",
            background: "transparent",
            border: "1px solid rgba(68, 157, 212, 0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#9aa0b4",
                boxShadow: "0 0 6px #9aa0b4",
                flexShrink: 0,
              }}
            />
            <Typography fontFamily={interFont} fontSize={13} color="#9aa0b4" fontWeight={500}>
              You're viewing <strong>sample data</strong> — your dashboard will populate as you
              create content and get learners.
            </Typography>
          </Stack>
          <Button
            size="small"
            startIcon={<CleaningServicesOutlinedIcon sx={{ fontSize: 15 }} />}
            onClick={() => {
              setShowingDummy(false);
              localStorage.setItem("mockDataCleared", "true");
            }}
            sx={{
              textTransform: "none",
              fontFamily: interFont,
              fontSize: 12,
              fontWeight: 600,
              color: "#fff",
              background: "#4BA5EB",
              border: "1px solid rgba(68, 157, 212, 0.3)",
              borderRadius: "7px",
              px: 1.8,
              py: 0.5,
              "&:hover": { background: "rgba(68, 157, 212, 0.3)" },
            }}
          >
            Clear Test Data
          </Button>
        </Box>
      )}

      {/* Top bar */}
      <Stack
        direction={isMobile ? "column" : "row"}
        alignItems={isMobile ? "flex-start" : "center"}
        justifyContent="space-between"
        mb={2}
        spacing={isMobile ? 1 : 0}
      >
        <Typography fontFamily={interFont} fontSize={24} fontWeight={600}>
          Your Overview
        </Typography>

        <Stack direction="row" spacing={1} width={isMobile ? "100%" : "auto"}>
          <Box
            sx={{
              background: "#2a2c36",
              borderRadius: "8px",
              p: "4px",
              display: "block",
              maxWidth: "100%",
            }}
          >
            <Tabs
              value={value}
              onChange={(_, newValue) => {
                setValue(newValue);
                setFilter(timeTabs[newValue].value);
              }}
              TabIndicatorProps={{ style: { display: "none" } }}
              variant="scrollable"
              scrollButtons={false}
              sx={{
                minHeight: "unset",
                maxWidth: "100%",
                "& .MuiTabs-scroller": { overflowX: "auto" },
                "& .MuiTabs-flexContainer": { gap: "4px", flexWrap: "nowrap" },
              }}
            >
              {timeTabs.map((t) => (
                <Tab
                  key={t.label}
                  label={t.label}
                  disableRipple
                  sx={{
                    minHeight: "unset",
                    textTransform: "none",
                    fontFamily: interFont,
                    fontSize: 13,
                    color: "#7A7A7A",
                    borderRadius: "6px",
                    px: 1.5,
                    py: 0.5,
                    "&.Mui-selected": {
                      background: "#0f1016",
                      border: `1px solid ${border}`,
                      color: "#fff",
                    },
                  }}
                />
              ))}
            </Tabs>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Button
              size="small"
              onClick={handleClick}
              sx={{
                textTransform: "none",
                fontFamily: interFont,
                fontSize: 12,
                color: "#fff",
                background: "#15151D",
                borderRadius: "6px",
                px: 1.5,
                border: `1px solid ${border}`,
                whiteSpace: "nowrap",
              }}
            >
              {startDate?.format("DD MMM")} – {endDate?.format("DD MMM")}
            </Button>

            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              <Box sx={{ p: 2, display: "flex", gap: 2 }}>
                <DatePicker
                  label="From"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                />
                <DatePicker
                  label="To"
                  value={endDate}
                  minDate={startDate || undefined}
                  onChange={(newValue) => setEndDate(newValue)}
                />
              </Box>
            </Popover>
          </LocalizationProvider>
        </Stack>
      </Stack>

      {/* Top 3 stat cards */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <StatCard
            title="Paid Learners"
            value={String(displayData?.paid_learners ?? 0)}
            growth={`+${displayData?.dashboard?.learners?.change_percent ?? 0}% Increase`}
            chartLabels={displayData?.charts?.paid_learners_chart?.labels || []}
            chartData={displayData?.charts?.paid_learners_chart?.data || []}
            isDummy={showingDummy}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard
            title="Total Revenue"
            value={String(displayData?.dashboard?.revenue?.total ?? 0)}
            growth={`+${displayData?.dashboard?.revenue?.change_percent ?? 0}% Increase`}
            chartLabels={displayData?.charts?.revenue_chart?.labels || []}
            chartData={displayData?.charts?.revenue_chart?.data || []}
            isDummy={showingDummy}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard
            title="Total Learners"
            value={String(displayData?.total_learners ?? 0)}
            growth={`+${displayData?.dashboard?.learners?.change_percent ?? 0}% Increase`}
            chartLabels={displayData?.charts?.learners_chart?.labels || []}
            chartData={(displayData?.charts?.learners_chart?.data || []).map(Number)}
            isDummy={showingDummy}
          />
        </Grid>
      </Grid>

      {/* Bottom small stat cards */}
      <Grid container spacing={2} mt={0.5}>
        {[
          { label: "Courses", value: displayData?.total_courses, icon: <SchoolOutlinedIcon fontSize="small" /> },
          { label: "Digital Twins", value: displayData?.digital_twins, icon: <SmartToyOutlinedIcon fontSize="small" /> },
          { label: "Videos", value: displayData?.total_episodes, icon: <PlayCircleOutlineIcon fontSize="small" /> },
          { label: "Blogs", value: displayData?.total_blogs, icon: <ArticleOutlinedIcon fontSize="small" /> },
          { label: "Audio", value: displayData?.total_audios, icon: <VolumeUpOutlinedIcon fontSize="small" /> },
          { label: "Documents", value: displayData?.total_documents, icon: <DescriptionOutlinedIcon fontSize="small" /> },
          { label: "Clips", value: displayData?.total_clips, icon: <ContentCutOutlinedIcon fontSize="small" /> },
          { label: "Images", value: displayData?.total_images, icon: <ImageOutlinedIcon fontSize="small" /> },
        ].map(({ label, value, icon }) => (
          <Grid item xs={12} md={3} key={label}>
            <SmallStat
              label={label}
              value={String(value ?? 0)}
              icon={icon}
              isDummy={showingDummy}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}